import { ChangeDetectionStrategy, Component, computed, effect, input, model, signal } from '@angular/core';
import { AvatarComponent } from '../avatar/avatar.component';

const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

/**
 * Avatar preview + "Upload" button. The chosen file is only kept locally (`file`, two-way);
 * the parent uploads it when it saves, then resets `file` to null.
 */
@Component({
  selector: 'app-avatar-picker',
  imports: [AvatarComponent],
  templateUrl: './avatar-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col items-center gap-2 shrink-0' },
})
export class AvatarPickerComponent {
  /** The saved thumbnail, shown until a new file is chosen. */
  readonly currentUrl = input<string | undefined>();
  readonly name = input('');
  readonly seed = input('');
  readonly fallback = input<'initials' | 'photo'>('initials');
  readonly disabled = input(false);
  readonly file = model<File | null>(null);

  protected readonly error = signal<string | null>(null);
  protected readonly previewUrl = computed(() => {
    const file = this.file();
    return file ? URL.createObjectURL(file) : null;
  });

  constructor() {
    // Free each preview's object URL when it's replaced or the picker is destroyed
    effect((onCleanup) => {
      const url = this.previewUrl();
      onCleanup(() => url && URL.revokeObjectURL(url));
    });
  }

  protected onSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = ''; // the same file can be picked again
    if (!file) {
      return;
    }

    this.error.set(
      !file.type.startsWith('image/')
        ? 'Please choose an image file.'
        : file.size > MAX_AVATAR_BYTES
          ? 'Image is too large. Please choose a file under 5 MB.'
          : null,
    );
    if (!this.error()) {
      this.file.set(file);
    }
  }
}
